import type { ReactNode } from 'react';
import styles from './notice.module.css';
import { cx } from './cx';

export type NoticeTone = 'info' | 'caution' | 'error' | 'positive' | 'media';

/**
 * A short statement about the current state.
 *
 * `live` opts into `role="status"`, and it is off by default on purpose: a notice that is already on
 * screen when the page renders must not be announced, while one that *appears* after an action —
 * "Recovered from expired media URLs" — should be. Getting that backwards makes a screen reader read
 * the whole page's caveats on every navigation.
 */
export interface NoticeProps {
  readonly children: ReactNode;
  readonly tone?: NoticeTone;
  readonly live?: boolean;
  readonly className?: string;
}

const TONE_CLASS: Readonly<Record<NoticeTone, string | undefined>> = {
  info: undefined,
  caution: styles.caution,
  error: styles.error,
  positive: styles.positive,
  media: styles.media,
};

export function Notice({ children, tone = 'info', live, className }: NoticeProps) {
  return (
    <p className={cx(styles.notice, TONE_CLASS[tone], className)} role={live === true ? 'status' : undefined}>
      {children}
    </p>
  );
}
